import { Component } from 'react';
import { container } from '../../../../context/module';
import { MetricsService } from '../../../../services/metrics.service';
import { MetricEventName } from '../../../../types/metric-events';
import { debounceTime } from 'rxjs/operators';

import { Subscription } from 'rxjs';
import { withRequestIdleCallback, withRequestIdleCallbackAsync } from '../../../../annotations/request-idle-callback.annotation';
import { lazyInject } from '../../../../context/module';
import { FormGroup } from '../../../../models/forms';
import { Logger } from '../../../../models/logger';
import { UploadInProgressFile } from '../../../../models/upload-in-progress-file';
import { AuthService } from '../../../../services/auth.service';
import { ScriptLoadService } from '../../../../services/script-load.service';
import { ProbedData } from '../../../../services/file-process.service';
import { UIContext } from '../../../../services/ui-context';
import { InputFileInfo } from '../../../../types/file-upload.types';
import { OptionalChildrenProps } from '../../../../types/react.types';
import { OrientationUpdateEvent, RotationAndFlip, ToType } from '../../../../types/types';
import { BrowserUtils } from '../../../../ui-utils/browser-utils';
import { includes, isEqual } from '../../../../utils/lodash-min';
import { ProcessorMetadataUtils } from '../../../../utils/processor-metadata.utils';
import { RxUtils } from '../../../../utils/rx-utils';
import { StringUtils } from '../../../../utils/string-utils';
import { untilDestroyed } from '../../../../utils/until-destroyed';
import { ConvertOptionsContextObserver, ConvertOptionsProvider, ConvertOptionsSummary } from '../../../convert-options-v2/convert-options.context';
import ProgressComponent from '../../progress/progress.component';
import styles from './image-display.component.module.scss';

declare var dataURLtoBlob;
declare var loadImage;

const margins = {
    "NO_MARGIN": {
        "left": 0,
        "top": 0,
        "right": 0,
        "bottom": 0
    },
    "NARROW_MARGIN": {
        "left": 0.5,
        "top": 0.5,
        "right": 0.5,
        "bottom": 0.5
    },
    "MODERATE_MARGIN": {
        "left": 0.75,
        "top": 1,
        "right": 0.75,
        "bottom": 1
    },
    "NORMAL_MARGIN": {
        "left": 1,
        "top": 1,
        "right": 1,
        "bottom": 1
    },
    "WIDE_MARGIN": {
        "left": 2,
        "top": 1,
        "right": 2,
        "bottom": 1
    },
}

const pageSizes = {
    "SAME_AS_IMAGE_SIZE": {
        "width": -1,
        "height": -1
    },
    "LETTER": {
        "width": 8.5,
        "height": 11
    },
    "TABLOID": {
        "width": 11,
        "height": 17
    },
    "LEDGER": {
        "width": 17,
        "height": 11
    },
    "LEGAL": {
        "width": 8.5,
        "height": 14
    },
    "EXECUTIVE": {
        "width": 7.25,
        "height": 10.5
    },
    "A3": {
        "width": 11.69,
        "height": 16.53
    },
    "A4": {
        "width": 8.27,
        "height": 11.69
    },
    "SCREEN": {
        "width": 6.5,
        "height": 5.18
    },
    "ARCH_A": {
        "width": 9,
        "height": 12
    },
    "ARCH_B": {
        "width": 12,
        "height": 18
    },
    "ISO_B5": {
        "width": 6.93,
        "height": 9.85
    },
    "ISO_B4": {
        "width": 9.85,
        "height": 13.9
    },
}

export const IMAGE_DEFAULT_MARGIN = {
    left: 0.5,
    top: 0.5,
    right: 0.5,
    bottom: 0.5,
};

export const IMAGE_DEFAULT_PAGE_SIZE = {
    width: 8.27,
    height: 11.69,
};

export const IMAGE_DEFAULT_ALIGNMENT = "CENTER_ALIGNMENT";

export const IMAGE_DEFAULT_PLACEMENT = "IMAGE_COVER_PLACEMENT";

export const IMAGE_DEFAULT_ORIENTATION = "PORTRAIT";


/**
 * EXIF Orientation Handling
 * =========================
 *
 * This component handles EXIF orientation for images using a graph-based approach.
 * The 8 EXIF orientations represent different combinations of rotation and horizontal flip:
 *
 * | Orientation | Rotation | Horizontal Flip |
 * |-------------|----------|-----------------|
 * | 1           | 0°       | No              |
 * | 2           | 0°       | Yes             |
 * | 3           | 180°     | No              |
 * | 4           | 180°     | Yes             |
 * | 5           | 90° CCW  | Yes             |
 * | 6           | 90° CW   | No              |
 * | 7           | 90° CW   | Yes             |
 * | 8           | 90° CCW  | No              |
 *
 * The Vertex class below creates a graph where:
 * - `next` edge = 90° clockwise rotation
 * - `flip` edge = horizontal mirror/flip
 */

export class Vertex {
    orientation: number;
    previous: Vertex;
    next: Vertex;
    flip: Vertex;

    constructor(orientation) {
        this.orientation = orientation;
    }

    setRelatives(previous, next, flip): void {
        this.previous = previous;
        this.next = next;
        this.flip = flip;
    }
}

// Remove when file_upload_manager.ts imported to this project
export type SupportedFileTypes = File | any;

interface ImageDisplayProps extends OptionalChildrenProps {
    convertOptionCategories: ConvertOptionsProvider;
    convertOptionsContext: ConvertOptionsContextObserver,
    file: any;
    uploadInProgressFile: UploadInProgressFile<SupportedFileTypes>;
    probedData: ProbedData;
    probing: boolean;
    toType: ToType | string;
    fromType: string[];
    processType?: string;
    inputFileInfo: InputFileInfo;
    removeFile: (data?: any) => void;
    orientationChanged: (data: OrientationUpdateEvent) => void;
    probe: (data: number) => void;
    onError?: () => void;
}

export class ImageDisplayComponent extends Component<ImageDisplayProps> {
    private readonly logger = Logger.getLogger('ImageDisplayComponent');
    static urlToImageCache = new Map<string, any>();

    private get imageCacheKey(): string {
        const file = this.props.uploadInProgressFile.getFile();
        return `${file.name}_${file.size}`;
    }
    static map = new Map<String, Vertex>();
    static mapLoaded = false;

    page: any;
    backgroundImg: any;
    backgroundSize: 'contain' | 'cover';
    backgroundPosition: 'top' | 'center' | 'bottom';
    currentOrientation = 1;
    originalOrientation = 1;

    rotationMap = new Map();
    flipMapH = new Map();
    flipMapV = new Map();

    toolsEnabled = false;

    count = 0;

    probeDurationOptionUUID = StringUtils.uuid();
    imageURL: any;
    sampleDurationFormGroup: FormGroup;

    imageBlobUrl: string;
    error = false;

    imageWidth = 0;
    imageHeight = 0;

    private containerHeight = 70;

    static initializeMap() {
        if (ImageDisplayComponent.mapLoaded) {
            return;
        }
        const one = new Vertex(1);
        const two = new Vertex(2);
        const three = new Vertex(3);
        const four = new Vertex(4);
        const five = new Vertex(5);
        const six = new Vertex(6);
        const seven = new Vertex(7);
        const eight = new Vertex(8);

        one.setRelatives(six, eight, two);
        two.setRelatives(five, seven, one);
        three.setRelatives(eight, six, four);
        four.setRelatives(seven, five, three);
        five.setRelatives(four, two, eight);
        six.setRelatives(three, one, seven);
        seven.setRelatives(two, four, six);
        eight.setRelatives(one, three, five);

        ImageDisplayComponent.map.set('1', one);
        ImageDisplayComponent.map.set('2', two);
        ImageDisplayComponent.map.set('3', three);
        ImageDisplayComponent.map.set('4', four);
        ImageDisplayComponent.map.set('5', five);
        ImageDisplayComponent.map.set('6', six);
        ImageDisplayComponent.map.set('7', seven);
        ImageDisplayComponent.map.set('8', eight);
        ImageDisplayComponent.mapLoaded = true;
    }

    /**
     * Calculates the rotation angle and flip needed to transform from initialOrientation to finalOrientation.
     *
     * IMPORTANT - Backend Application Order:
     * ======================================
     * The returned RotationAndFlip should be applied in this order:
     *
     * 1. IF isFliped === 'true':  Apply HORIZONTAL FLIP first
     * 2. THEN:                    Apply ROTATION by 'angle' degrees CLOCKWISE
     *
     * Examples:
     * - { angle: '90', isFliped: 'false' }  → Rotate 90° clockwise
     * - { angle: '0', isFliped: 'true' }    → Flip horizontally only
     * - { angle: '90', isFliped: 'true' }   → Flip horizontally, THEN rotate 90° clockwise
     * - { angle: '180', isFliped: 'true' }  → Flip horizontally, THEN rotate 180°
     *
     * This follows the EXIF orientation standard where flip is applied before rotation.
     */
    static getRotationAndFlip(initialOrientation: string, finalOrientation: string): RotationAndFlip {
        const source: Vertex = ImageDisplayComponent.map.get(initialOrientation);
        let rotation = 360;
        if (initialOrientation === finalOrientation) {
            return {
                angle: '0',
                isFliped: 'false',
                orientation: finalOrientation,
            };
        }

        let i = 3;
        let temp: Vertex = source;
        while (i > 0) {
            rotation = rotation - 90;
            temp = temp.next;
            if ((temp.orientation + '') === finalOrientation) {
                return {
                    angle: rotation + '',
                    isFliped: 'false',
                    orientation: finalOrientation,
                };
            }
            i--;
        }

        temp = source.flip;
        rotation = 0;
        if ((temp.orientation + '') === finalOrientation) {
            return {
                angle: rotation + '',
                isFliped: 'true',
                orientation: finalOrientation,
            };
        }

        i = 3;
        while (i > 0) {
            rotation = rotation + 90;
            temp = temp.next;
            if ((temp.orientation + '') === finalOrientation) {
                return {
                    angle: rotation + '',
                    isFliped: 'true',
                    orientation: finalOrientation,
                };
            }
            i--;
        }

        return {
            angle: '0',
            isFliped: 'false',
            orientation: finalOrientation,
        };
    }

    @lazyInject(AuthService) public auth: AuthService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(UIContext) private uiContext: UIContext;

    private subs: Subscription[] = [];
    private lastEmittedOptionsSummary: ConvertOptionsSummary;

    constructor(props) {
        super(props);
        ImageDisplayComponent.initializeMap();
        // https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images
        this.rotationMap.set(1, 8);
        this.rotationMap.set(8, 3);
        this.rotationMap.set(3, 6);
        this.rotationMap.set(6, 1);

        this.rotationMap.set(2, 5);
        this.rotationMap.set(5, 4);
        this.rotationMap.set(4, 7);
        this.rotationMap.set(7, 2);

        this.flipMapH.set(1, 2);
        this.flipMapH.set(2, 1);
        this.flipMapH.set(3, 4);
        this.flipMapH.set(4, 3);

        this.flipMapH.set(6, 5);
        this.flipMapH.set(5, 6);

        this.flipMapH.set(8, 7);
        this.flipMapH.set(7, 8);

        this.flipMapV.set(6, 7);
        this.flipMapV.set(2, 3);
        this.flipMapV.set(3, 2);
        this.flipMapV.set(7, 6);

        this.flipMapV.set(1, 4);
        this.flipMapV.set(4, 1);
        this.flipMapV.set(8, 5);
        this.flipMapV.set(5, 8);

        if (BrowserUtils.hasWindow()) {
            const optionChangedSub = this.props.convertOptionsContext.modelChangeObserver.pipe(
                untilDestroyed(this),
                debounceTime(100),
            ).subscribe((optionsSummary) => {
                this.lastEmittedOptionsSummary = optionsSummary;
                this.convertOptionCategoriesChanged(optionsSummary);
            });
            this.subs.push(optionChangedSub);

            const uploadInProgressFileChanged = this.props.uploadInProgressFile.changes.pipe(
                untilDestroyed(this),
                debounceTime(100),
            ).subscribe(() => {
                this.forceUpdate();
            });
            this.subs.push(uploadInProgressFileChanged);
        }
    }

    private orientaionToRotationAndFlip() {
        return ImageDisplayComponent.getRotationAndFlip(this.originalOrientation + '', this.currentOrientation + '');
    }

    componentDidMount() {
        if (!this.props.uploadInProgressFile.completed) {
            // completed is used as the key for this component. Thus, this makes multiple requests when completed state changes.
            return;
        }
        this.load()
            .then(() => this.refresh())
            .catch((e) => {
                this.logger.error(e);
                this.error = true;
                this.page = { size: { outer: { width: 60, height: 60 }, inner: { x: 0, y: 0, width: 60, height: 60 } } };
                this.forceUpdate();
            }).finally(() => {
                this.convertOptionCategoriesChanged(this.lastEmittedOptionsSummary);
                this.sampleDurationFormGroup = new FormGroup();
                this.sampleDurationFormGroup.setControl('sampleDuration');
                this.sampleDurationFormGroup.setValue({
                    sampleDuration: 40,
                });
            });
    }

    componentWillUnmount(): void {
        // Only revoke URLs not stored in the shared cache
        if (this.imageBlobUrl && this.imageBlobUrl !== this.imageURL) { URL.revokeObjectURL(this.imageBlobUrl); }
        if (this.imageURL && !ImageDisplayComponent.urlToImageCache.has(this.imageCacheKey)) { URL.revokeObjectURL(this.imageURL); }
        this.subs.forEach(sub => {
            RxUtils.safeCloseSubscriber(sub);
        })
    }

    @withRequestIdleCallback
    refresh() {
        const blobURL = ImageDisplayComponent.urlToImageCache.get(this.imageCacheKey);
        const image = loadImage(
            blobURL || this.imageURL,
            (canvas: HTMLCanvasElement, data) => {
                const t = dataURLtoBlob(canvas.toDataURL());
                if (this.imageBlobUrl) { URL.revokeObjectURL(this.imageBlobUrl); }
                const imageURL = URL.createObjectURL(t);
                this.imageBlobUrl = imageURL;
                this.logger.trace7({ message: `${this.currentOrientation}`, data: this.orientaionToRotationAndFlip() });
                const updatedInfo = {
                    file: this.props.file,
                    orientation: this.orientaionToRotationAndFlip(),
                };

                this.props.orientationChanged(updatedInfo);
                ImageDisplayComponent.urlToImageCache.set(this.imageCacheKey, this.imageURL);
                this.backgroundImg = 'url(' + imageURL + ')';
                setTimeout(() => this.forceUpdate(), 10);
            },
            {
                orientation: this.currentOrientation,
                canvas: true,
                maxWidth: 200,
            },
        );
    }

    /**
     * Where `loadImage` should read the thumbnail from.
     *
     * Signed-in users get the token in an `Authorization` header instead of the query string.
     * That means fetching the bytes ourselves and handing `loadImage` a Blob, because an
     * `<img>`-style load cannot carry headers -- which is why the token was in the URL to begin
     * with. It kept a bearer JWT in every proxy, CDN and access log between the user and the
     * edge, and it added ~317 characters to a URL already over 1,100.
     *
     * Anonymous users have no token, so their URL is unchanged.
     */
    private async resolveThumbnailSource(url: string): Promise<string | Blob> {
        const authToken = this.auth.getToken();
        if (!authToken) return url;
        try {
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${authToken}` },
                credentials: 'include',
            });
            if (response.ok) return await response.blob();
            // 401/403 is the one case where the header itself is plausibly the problem, so it
            // is the only status worth spending a second request on.
            if (response.status === 401 || response.status === 403) {
                return this.legacyTokenInUrl(url, authToken);
            }
            throw new Error(`thumbnail ${response.status}`);
        } catch (e) {
            // Network error or a CORS preflight the edge did not allow. Fall back rather than
            // lose the preview; the retry and error handling above cover a genuine failure.
            this.logger.trace7(`Thumbnail header fetch failed, falling back: ${e}`);
            return this.legacyTokenInUrl(url, authToken);
        }
    }

    /**
     * DEPRECATED -- remove after 2026-10-26.
     *
     * The token in the query string. Kept only so a signed-in user whose network blocks the
     * header-authenticated fetch still sees previews during the transition. The server side of
     * this (the `authorization` request-parameter branch in `TransactionContextImpl`) is dated
     * to go at the same time; both must be removed together, and nothing else in the front end
     * puts a token in a URL.
     */
    private legacyTokenInUrl(url: string, authToken: string): string {
        return `${url}&authorization=${authToken}`;
    }

    async load(): Promise<any> {
        const readyPromise = this.props.uploadInProgressFile.whenResponseReady();
        await readyPromise;
        const processorMetadata = this.props.uploadInProgressFile.getMetadata().processorMetadataList[0];

        const token = this.props.uploadInProgressFile.getResponse().token;
        const gcName = this.props.uploadInProgressFile.getResponse().gcName || processorMetadata.fileMetadata.gcName;

        const url = ProcessorMetadataUtils.getThumbnailEndpoint(processorMetadata)
            + `/thumbnail?token=${token}&gcName=${gcName}` +
            `&uploadType=${processorMetadata.uploadType}&safeName=${processorMetadata.fileMetadata.safeName}`;

        await this.scriptLoadService.waitForGlobal('loadImage');
        const blobURL = ImageDisplayComponent.urlToImageCache.get(this.imageCacheKey);
        // Resolved before the promise below so the request can carry a header, which an
        // <img>-style load cannot do. A cached object URL short-circuits it entirely.
        const source = blobURL || await this.resolveThumbnailSource(url);

        return new Promise<void>((resolve, reject) => {
            const orientation = this.props.uploadInProgressFile.getOrientation();
            const orientationNumber = (orientation && orientation.orientation && (Number(orientation.orientation))) || undefined;
            loadImage(
                source,
                (canvas: HTMLCanvasElement, data) => {
                    if (canvas && canvas.toDataURL) {
                        const t = dataURLtoBlob(canvas.toDataURL());
                        if (this.imageURL) { URL.revokeObjectURL(this.imageURL); }
                        this.imageURL = URL.createObjectURL(t);
                        this.logger.trace7(`${this.currentOrientation}`);
                        this.currentOrientation = orientationNumber || (data.exif && data.exif.get('Orientation')) || 1;
                        if (!blobURL) {
                            // Only set this once
                            this.originalOrientation = this.currentOrientation;
                        }
                        if (!orientationNumber) {
                            // This component was load with a orientation. No  need to notify that
                            this.props.orientationChanged({
                                file: this.props.file,
                                orientation: this.orientaionToRotationAndFlip(),
                            });
                        }
                        this.backgroundImg = 'url(' + this.imageURL + ')';
                        this.imageWidth = canvas.width;
                        this.imageHeight = canvas.height;
                        ImageDisplayComponent.urlToImageCache.set(this.imageCacheKey, this.imageURL);
                        resolve();
                    } else {
                        reject();
                    }
                },
                {
                    // what stored on the map is in current orientation, doing this will re-orient it again
                    // ...(orientationNumber ? { orientation: orientationNumber } : undefined),
                    canvas: true,
                    meta: true,
                    maxWidth: 200,
                },
            );
        }).then(() => {
            this.toolsEnabled = true;
            this.thumbnailAttempts = 0;
        }).catch(() => this.onThumbnailFailed(url));
    }

    /**
     * How many times this card has tried to fetch its thumbnail.
     *
     * A thumbnail is one GET to a per-upload edge host (`ncf-ec2-east-NN-hv.xconvert.com`), and
     * a single failure used to leave a broken preview until the user noticed the Retry button.
     * On a corporate proxy or a slow link a transient failure is ordinary, so one silent retry
     * turns most of them into a preview the user never knew was at risk.
     */
    private thumbnailAttempts = 0;

    /** Delay before the automatic retry. One short pause, not a backoff loop. */
    private static readonly THUMBNAIL_RETRY_DELAY_MS = 1200;

    private onThumbnailFailed(url: string) {
        this.thumbnailAttempts += 1;
        if (this.thumbnailAttempts < 2) {
            setTimeout(() => {
                // Guard against a card removed while the retry was pending.
                if (!this.thumbnailAttempts) return;
                this.load().then(() => this.refresh()).catch(() => { /* handled below */ });
            }, ImageDisplayComponent.THUMBNAIL_RETRY_DELAY_MS);
            return;
        }
        this.error = true;
        this.reportThumbnailFailure(url);
        this.forceUpdate();
    }

    /**
     * Tell the backend a preview could not be fetched.
     *
     * There was no signal for this anywhere: `frontend-events` carries validation and suggestion
     * events only, so a customer reporting "the images fail to load" left nothing to look at.
     * The host matters most -- thumbnails come from whichever edge served the upload, and a
     * network that blocks those subdomains is the likeliest cause we cannot see from here.
     */
    private reportThumbnailFailure(url: string) {
        try {
            let host = '';
            try { host = new URL(url).host; } catch { /* leave blank */ }
            container.get(MetricsService).emit(MetricEventName.THUMBNAIL_LOAD_FAILED, {
                edgeHost: host,
                attempts: this.thumbnailAttempts,
                urlLength: url.length,
            });
        } catch { /* telemetry is never load-bearing */ }
    }

    @withRequestIdleCallbackAsync
    async rotateFileFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not rotate image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.rotationMap.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    @withRequestIdleCallbackAsync
    async flipFileHFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not filp image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.flipMapH.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    @withRequestIdleCallbackAsync
    async flipFileVFn(processRequest: boolean, e?) {
        e?.stopPropagation();
        if (!processRequest) {
            this.uiContext.errorToast(
                'Upload In Progress',
                'You can not filp image until file upload is completed. Wait for a thumbnail to be shown.',
                false,
                false);
        }
        const nextOrientation = this.flipMapV.get(this.currentOrientation);
        this.currentOrientation = nextOrientation;
        this.refresh();
    }

    convertOptionCategoriesChanged(summary: ConvertOptionsSummary) {
        // this.refresh();
        // create page rect
        if (!summary || !pageSizes || !margins) {
            return;
        }
        const pageSize = pageSizes[summary.pageSizePreset] ?? IMAGE_DEFAULT_PAGE_SIZE;
        const margin = margins[summary.pageMargin] ?? IMAGE_DEFAULT_MARGIN;
        const isLandscape = summary.pageLayout === 'LANDSCAPE';

        // const pageSize: any = !pageOptions ? IMAGE_DEFAULT_PAGE_SIZE :
        //     pageOptions.formGroups.find(item => item.formGroupName === 'pageSize').selections.find(item => item.selected);
        // const margin: any = !pageOptions ? IMAGE_DEFAULT_MARGIN :
        //     pageOptions.formGroups.find(item => item.formGroupName === 'margin').selections.find(item => item.selected);
        // const isLandscape: boolean = !pageOptions ? true :
        //     pageOptions.formGroups.find(item => item.formGroupName === 'pageOrientation')
        //         .selections.find(item => item.selected).formName as any === 'landscape';


        const page = {} as any;
        const targetImageDimensions = {
            width: pageSize.width == -1 ? this.imageWidth : pageSize.width,
            height: pageSize.height == -1 ? this.imageHeight : pageSize.height,
        };

        const normalizer = this.containerHeight / Math.max(targetImageDimensions.width, targetImageDimensions.height);
        if (isLandscape) {
            page.size = {
                outer: {
                    x: 0,
                    y: 0,
                    width: targetImageDimensions.height * normalizer,
                    height: targetImageDimensions.width * normalizer,
                },
                inner: {
                    x: margin.left * normalizer,
                    y: margin.top * normalizer,
                    width: (targetImageDimensions.height - (margin.left + margin.right)) * normalizer,
                    height: (targetImageDimensions.width - (margin.top + margin.bottom)) * normalizer,
                },
            };
        } else {
            page.size = {
                outer: {
                    x: 0,
                    y: 0,
                    width: targetImageDimensions.width * normalizer,
                    height: targetImageDimensions.height * normalizer,
                },
                inner: {
                    x: margin.left * normalizer,
                    y: margin.top * normalizer,
                    width: (targetImageDimensions.width - (margin.left + margin.right)) * normalizer,
                    height: (targetImageDimensions.height - (margin.top + margin.bottom)) * normalizer,
                },
            };
        }

        const imagePlacement = summary.imagePlacement ?? IMAGE_DEFAULT_PLACEMENT;
        const imageAlignment = summary.imageAlignment ?? IMAGE_DEFAULT_ALIGNMENT;

        // const imageOptions = this.props.convertOptionCategories.formCategories.find(item => item.category === 'imageOptions');
        // const imagePlacement: any = !imageOptions ? IMAGE_DEFAULT_PLACEMENT
        //     : imageOptions.formGroups.find(item => item.formGroupName === 'imagePlacement')
        //         .selections.find(item => item.selected);
        // const imageAlignment: any = !imageOptions ? IMAGE_DEFAULT_ALIGNMENT
        //     : imageOptions.formGroups.find(item => item.formGroupName === 'imageAlignment')
        //         .selections.find(item => item.selected);

        const backgroundSize = imagePlacement === 'IMAGE_CONTAINED_PLACEMENT' ? 'contain' : 'cover';
        const backgroundPosition = imageAlignment === 'CENTER_ALIGNMENT' ? 'center' : (imageAlignment === 'TOP_ALIGNMENT' ? 'top' : 'bottom');
        if (!this.isSame(this.page, page)
            || !this.isSame(this.backgroundSize, backgroundSize)
            || !this.isSame(this.backgroundPosition, backgroundPosition)) {
            this.page = page;
            this.backgroundSize = backgroundSize;
            this.backgroundPosition = backgroundPosition;
            this.forceUpdate();
        }

    }

    isSame(a, b) {
        return isEqual(a, b);
    }

    // previousInputFileInfo = null;
    // shouldComponentUpdate(nextProps: ImageDisplayProps) {
    //     const hasChanged = !isEqual(nextProps.inputFileInfo, this.previousInputFileInfo?.inputFileInfo);
    //     this.previousInputFileInfo = cloneDeep(nextProps.inputFileInfo);
    //     return hasChanged;
    // }

    shouldShowRotateAndFlip() {
        if (this.props.toType === 'image' && includes(this.props.fromType, 'video')) {
            // Pallet(-lavfi) with -vf not supported yet
            return false;
        }
        if (String(this.props.processType).toLowerCase() === 'compress'
            && String(this.props.toType).toLowerCase() === 'gif') {
            // Orientation tools don't belong on the compress-gif cards (owner, 2026-08-23)
            return false;
        }
        return true;
    }

    render() {
        if (!this.page) {
            return (
                <div className={styles['page']}>
                    <div className={styles['page-outer']} style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ProgressComponent
                            value={this.props.uploadInProgressFile.getProgress()}
                            postFix='%'
                            preFix="">
                        </ProgressComponent>
                    </div>
                </div>
            );
        }

        return (
            <div className={`${styles['page']}`}>
                <div className={styles['page-outer']} style={{ width: `${this.page.size.outer.width}px`, height: `${this.page.size.outer.height}px` }}>
                    <div className={styles['page-inner']} style={{
                        left: `${this.page.size.inner.x}px`, top: `${this.page.size.inner.y}px`,
                        width: `${this.page.size.inner.width}px`,
                        height: `${this.page.size.inner.height}px`,
                        backgroundImage: `${this.backgroundImg}`,
                        backgroundSize: `${this.backgroundSize}`,
                        backgroundPosition: `${this.backgroundPosition}`
                    }}>
                        {this.error && (
                            // Only the preview failed. The file uploaded and converts normally --
                            // verified by running a PNG->WebP conversion with the thumbnail
                            // request forced to fail throughout. Without saying so, a broken
                            // thumbnail reads as a broken file, which is how this was reported.
                            <div className={styles['error']}
                                title="Preview couldn't load. Your file is fine — you can still convert it."
                                role="img"
                                aria-label="Preview unavailable. Your file is fine and can still be converted.">
                                <img src="/icons/svg/generic-file.svg" alt="" aria-hidden="true" />
                                <button
                                    className={styles['retry-btn']}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.error = false;
                                        this.thumbnailAttempts = 0;
                                        this.forceUpdate();
                                        this.load()
                                            .then(() => this.refresh())
                                            .catch(() => { this.error = true; this.forceUpdate(); })
                                            .finally(() => this.forceUpdate());
                                    }}
                                >Retry</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
